import React from 'react';
import PropTypes from 'prop-types';
import './Button.scss';
import Typography from '../Typography';
import { FlexRow, FlexColumn } from '../GridsAndLayouts/GridsAndLayouts';
import { SpinnerIcon } from '../Icons';

/** Spinner diameter (px) aligned to each button size token. */
const BUTTON_SPINNER_SIZE = {
  sm: 14,
  base: 16,
  l: 24,
};

/** Loader ring; stroke colors come from `--ds-button-loader-*` or variant overrides when `loading`. */
const ButtonSpinner = ({ spinnerSize = BUTTON_SPINNER_SIZE.base }) => (
  <FlexColumn
    inline
    fullWidth={false}
    align="center"
    justify="center"
    className="ds-button__spinner-frame"
    role="status"
    aria-label="Loading"
  >
    <FlexRow inline fullWidth={false} align="start" justify="center" className="ds-button__spinner-rotator" aria-hidden>
      <SpinnerIcon className="ds-button__spinner-svg" size={spinnerSize} />
    </FlexRow>
  </FlexColumn>
);

const Button = React.forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'base',
      icon,
      iconPosition = 'left',
      justify,
      disabled = false,
      loading = false,
      fullWidth = false,
      className = '',
      onClick,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ds-button',
      `ds-button--${variant}`,
      `ds-button--${size}`,
      icon && !children && !loading && 'ds-button--icon-only',
      justify && `ds-button--justify-${justify}`,
      disabled && 'ds-button--disabled',
      loading && 'ds-button--loading',
      fullWidth && 'ds-button--full-width',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleClick = (e) => {
      if (disabled || loading) {
        e.preventDefault();
        return;
      }
      if (onClick) {
        onClick(e);
      }
    };

    const showIconLeft = !loading && icon && iconPosition === 'left';
    const showIconRight = !loading && icon && iconPosition === 'right';

    let body = null;
    if (!loading && children != null && children !== false) {
      if (variant === 'link' || variant === 'tertiary' || variant === 'white') {
        body = children;
      } else {
        body = (
          <Typography component="div" className="ds-button__text">
            {children}
          </Typography>
        );
      }
    } else if (!loading) {
      body = children;
    }

    return (
      <button
        ref={ref}
        type="button"
        className={classNames}
        disabled={disabled || loading}
        onClick={handleClick}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && (
          <FlexRow inline fullWidth={false} align="center" justify="center" className="ds-button__spinner">
            <ButtonSpinner spinnerSize={BUTTON_SPINNER_SIZE[size] ?? BUTTON_SPINNER_SIZE.base} />
          </FlexRow>
        )}
        {showIconLeft && (
          <FlexRow
            inline
            fullWidth={false}
            align="center"
            justify="center"
            className="ds-button__icon ds-button__icon--left"
          >
            {icon}
          </FlexRow>
        )}
        {body}
        {showIconRight && (
          <FlexRow
            inline
            fullWidth={false}
            align="center"
            justify="center"
            className="ds-button__icon ds-button__icon--right"
          >
            {icon}
          </FlexRow>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

Button.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf([
    'primary',
    /** Vertical gradient from `--ds-theme-action-primary-gradient`; same metrics as primary */
    'linear',
    /** White surface, purple label (`--pal-grey-purple-700`); pairs with `linear` layouts */
    'linear-outline',
    /** Pill tag — purple (#A514BE) on purple tint surface (`--pal-purple-a50`) */
    'tag-purple',
    /** Pill tag — blue (#293EF8) on blue tint surface (`--pal-blue-a50`) */
    'tag-blue',
    'secondary',
    'tertiary',
    'white',
    'link',
    'positive',
    'danger',
    /** Pill-shaped outline button (fully rounded ends) */
    'circular',
    /** Pill-shaped filled primary button */
    'circular-primary',
    /** Pill-shaped filled tertiary / muted button */
    'circular-tertiary',
  ]),
  size: PropTypes.oneOf(['sm', 'base', 'l']),
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  /** Controls flex layout inside the button; defaults to centered when omitted. */
  justify: PropTypes.oneOf(['start', 'between', 'end', 'center']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

Button.defaultProps = {
  children: undefined,
  variant: 'primary',
  size: 'base',
  icon: undefined,
  iconPosition: 'left',
  justify: undefined,
  disabled: false,
  loading: false,
  fullWidth: false,
  className: '',
  onClick: undefined,
};

export default Button;
